import { useRef, useState } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { HrSystemConflictCard } from '@lp/features/Scheduler/components';
import { CSShiftCellDropdown } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/components';
import { csShiftOptionTypeNames } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import { isCSDeductionRulesMode } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import {
  getCSShiftSetData,
  getShiftOptionFromShift,
  getShiftOptionIconColor,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/utils';
import { toLocalDateFromApiDateKey } from '@lp/features/Scheduler/utils';
import {
  CSResolveHrSystemConflict,
  CSResourceCSShiftTypeHrSystemConflict,
  CSShift,
  CSShiftSelectPayload,
  ScheduleHrSystemConflictType,
} from '@/api/apis/resources/schedule';
import { shiftTimeGroupNames, shiftTypeNames } from '@/constants/schedule.constants';

type CSHrSystemConflictCardProps = {
  conflict: CSResourceCSShiftTypeHrSystemConflict;
  onConflictResolve: (resolveData: CSResolveHrSystemConflict) => void;
};

export const CSHrSystemConflictCard = ({ conflict, onConflictResolve }: CSHrSystemConflictCardProps) => {
  const { conflictId, type, resource, hrSystemShiftType, day } = conflict;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const [shiftPayload, setShiftPayload] = useState<CSShiftSelectPayload>();
  const shiftOption = getShiftOptionFromShift(shiftPayload as CSShift);
  const textColor = getShiftOptionIconColor(shiftOption);
  const isRemoved = type === ScheduleHrSystemConflictType.HrSystemShiftTypeRemoved;
  const usesDeductionRules = isCSDeductionRulesMode(toLocalDateFromApiDateKey(day));

  const onApply = () => {
    const payload = isRemoved ? shiftPayload : ({ shiftType: hrSystemShiftType } as CSShiftSelectPayload);
    if (payload) {
      const setShiftData = getCSShiftSetData(payload, resource, resource.hubId, resource.regionId);
      onConflictResolve({ conflictId, setShiftData });
    }
  };

  return (
    <HrSystemConflictCard conflict={conflict} isApplyDisabled={isRemoved && !shiftPayload} onApply={onApply}>
      {isRemoved ? (
        <>
          <button
            ref={triggerRef}
            className={cn('flex text-m-r hover:underline', shiftOption ? textColor : 'text-base-25')}
            onClick={toggleIsOpen}
          >
            <span>
              {shiftOption ? csShiftOptionTypeNames[shiftOption] : 'No shift'}
              {usesDeductionRules && shiftPayload?.timeGroup && ` · ${shiftTimeGroupNames[shiftPayload.timeGroup]}`}
            </span>
            <MdOutlineKeyboardArrowDown size={18} className={cn({ 'rotate-180': isOpen })} />
          </button>
          {isOpen && (
            <CSShiftCellDropdown
              triggerRef={triggerRef}
              title="Pick Shift"
              isPlacementBottom
              isOpen={isOpen}
              onOpenChange={toggleIsOpen}
              shiftData={shiftPayload as CSShift} // Can omit other fields in this case
              onShiftChange={setShiftPayload}
              canReset
              allowedOrgans={resource.organs}
              canPhoneSupport={resource.canPhoneSupport}
              resourceTimeGroup={resource.shiftTimeGroup}
              showTimeGroup={usesDeductionRules}
            />
          )}
        </>
      ) : (
        <span>{shiftTypeNames[hrSystemShiftType]}</span>
      )}
    </HrSystemConflictCard>
  );
};
